
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/auth/useAuth";
import { Spinner } from "@/components/ui/spinner";

// Form schema for login
const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

// Form schema for signup
const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

// Form schema for password reset request
const resetPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type LoginFormValues = z.infer<typeof loginSchema>;
type SignupFormValues = z.infer<typeof signupSchema>;
type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "signup" | "reset">("login");
  const [authError, setAuthError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Redirect if already logged in
  useEffect(() => {
    if (user && !authLoading) {
      setRedirecting(true);
      console.log("User already authenticated, redirecting to contacts");
      
      // Use a timeout to ensure UI shows redirecting state
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 500);
    }
  }, [user, authLoading, navigate]);

  // Login form
  const loginForm = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Signup form
  const signupForm = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Reset password form
  const resetPasswordForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  // Handle login
  const handleLogin = async (values: LoginFormValues) => {
    try {
      setIsLoading(true);
      setAuthError(null);
      
      console.log("Attempting to log in with email:", values.email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        console.error("Login error:", error);
        setAuthError(error.message);
        toast.error(error.message);
        return;
      }

      console.log("Login successful, user:", data.user?.email);
      toast.success("Logged in successfully!");
      
      // Navigate happens in the useEffect
    } catch (error: any) {
      console.error("Login error:", error);
      setAuthError(error.message || "An error occurred during login");
      toast.error("An error occurred during login.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async (values: SignupFormValues) => {
    try {
      setIsLoading(true);
      setAuthError(null);
      
      console.log("Attempting to sign up with email:", values.email);
      const { data, error } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        console.error("Signup error:", error);
        setAuthError(error.message);
        toast.error(error.message);
        return;
      }

      if (data.user?.identities?.length === 0) {
        toast.error("This email is already registered. Please log in instead.");
        setActiveTab("login");
        return;
      }

      console.log("Signup successful, user:", data.user?.email);
      toast.success("Check your email for a confirmation link!");
      // Stay on the same page so they can log in after verifying
    } catch (error: any) {
      console.error("Signup error:", error);
      setAuthError(error.message || "An error occurred during signup");
      toast.error("An error occurred during signup.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle password reset request
  const handleResetPassword = async (values: ResetPasswordFormValues) => {
    try {
      setIsLoading(true);
      setAuthError(null);
      
      console.log("Requesting password reset for email:", values.email);
      const { error } = await supabase.auth.resetPasswordForEmail(values.email, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) {
        console.error("Password reset error:", error);
        setAuthError(error.message);
        toast.error(error.message);
        return;
      }

      console.log("Password reset email sent to:", values.email);
      toast.success("Password reset instructions sent to your email!");
      setResetSent(true);
    } catch (error: any) {
      console.error("Password reset error:", error);
      setAuthError(error.message || "An error occurred requesting password reset");
      toast.error("An error occurred requesting password reset.");
    } finally {
      setIsLoading(false);
    }
  };

  // If we're in a loading state, show a loading indicator
  if (authLoading || redirecting) {
    return (
      <div className="container flex flex-col items-center justify-center min-h-screen py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="lg" />
          <p className="text-lg">
            {redirecting ? "Redirecting to app..." : "Checking authentication..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container flex flex-col items-center justify-center min-h-screen py-12">
      <Button 
        variant="ghost" 
        className="absolute top-4 left-4 flex items-center gap-2"
        onClick={() => navigate("/")}
      >
        <ArrowLeft className="h-4 w-4" /> Back to Home
      </Button>
      
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Welcome</CardTitle>
          <CardDescription className="text-center">
            Sign in to your account or create a new one
          </CardDescription>
        </CardHeader>
        
        {authError && (
          <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
            {authError}
          </div>
        )}
        
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "login" | "signup" | "reset")} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
            <TabsTrigger value="reset">Reset</TabsTrigger>
          </TabsList>
          
          <TabsContent value="login">
            <CardContent className="pt-4">
              <Form {...loginForm}>
                <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                  <FormField
                    control={loginForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="your.email@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={loginForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <Spinner size="sm" /> Logging in...
                      </span>
                    ) : "Login"}
                  </Button>

                  <div className="text-center mt-2">
                    <Button 
                      variant="link" 
                      type="button"
                      className="text-sm text-muted-foreground"
                      onClick={() => setActiveTab("reset")}
                    >
                      Forgot your password?
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </TabsContent>
          
          <TabsContent value="signup">
            <CardContent className="pt-4">
              <Form {...signupForm}>
                <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                  <FormField
                    control={signupForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input placeholder="your.email@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={signupForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={signupForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="••••••••" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <Spinner size="sm" /> Creating account...
                      </span>
                    ) : "Create Account"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </TabsContent>

          <TabsContent value="reset">
            <CardContent className="pt-4">
              {resetSent ? (
                <div className="p-4 space-y-4">
                  <div className="text-center text-green-600 mb-4">
                    <p className="text-lg font-medium">Password Reset Email Sent</p>
                    <p className="text-sm mt-2">Please check your email for instructions to reset your password.</p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setResetSent(false);
                      setActiveTab("login");
                    }}
                  >
                    Return to Login
                  </Button>
                </div>
              ) : (
                <Form {...resetPasswordForm}>
                  <form onSubmit={resetPasswordForm.handleSubmit(handleResetPassword)} className="space-y-4">
                    <FormField
                      control={resetPasswordForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="your.email@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <span className="flex items-center gap-2">
                          <Spinner size="sm" /> Sending reset link...
                        </span>
                      ) : "Send Reset Link"}
                    </Button>

                    <div className="text-center mt-2">
                      <Button 
                        variant="link" 
                        type="button"
                        className="text-sm text-muted-foreground"
                        onClick={() => setActiveTab("login")}
                      >
                        Back to Login
                      </Button>
                    </div>
                  </form>
                </Form>
              )}
            </CardContent>
          </TabsContent>
        </Tabs>
        
        <CardFooter className="flex flex-col space-y-2 mt-2">
          <div className="text-sm text-muted-foreground text-center">
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </div>
          
          <div className="text-xs text-muted-foreground text-center mt-4">
            <p>Having trouble logging in? Make sure you've confirmed your email.</p>
            <p className="mt-1">If you're testing the app, you might need to 
            disable email confirmation in Supabase.</p>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Auth;
